import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Spinner } from '@components/atoms'
import { customerApi, ResType } from '@api'
import {
	FORUM_URL,
	POST_LOGIN_REDIRECT_KEY,
	pickSiteToken,
	dropSiteToken,
	clearSiteSession,
} from '@configs'
import { persistor } from '@redux/index'
import { Logger } from '@utils'

/**
 * `/sso` — 포럼(Discourse) 로그인 착지점. 화면이 없다 (judahsWeb/FORUM_PLAN.md §11).
 *
 * Discourse 가 "이 방문자가 누구냐" 를 물으러 `?sso=…&sig=…` 를 달아 여기로 보낸다. 우리 인증은
 * 쿠키가 아니라 브라우저가 든 JWT 라서 API 로 바로 보내면 API 가 사람을 못 알아본다 — 그래서
 * 프론트 라우트가 한 단계 끼어 토큰을 실어 API 에 넘기고, API 가 만든 서명된 주소로 되돌아간다.
 *
 *   ?logout=1  → 포럼에서 로그아웃하고 돌아온 것이다(Discourse `logout_redirect`). 사이트 세션까지
 *                지우고 포럼으로 돌려보낸다 — 안 지우면 다음 Login 클릭이 남은 세션으로 그냥 다시
 *                들어가서 계정을 고를 기회가 없다.
 *   토큰 없음  → 이 주소를 적어 두고 /customer/login 으로. 로그인이 끝나면 여기로 되돌아온다.
 *   토큰 있음  → POST /customer-auth/discourse-connect → redirectUrl 로 이동 → Discourse 로그인 완료.
 *   401        → 토큰이 죽었거나 `type` 없는 구 토큰. 세션을 지우고 로그인 페이지로 — 안 지우면
 *                가드가 "로그인됨" 으로 보고 홈으로 튕겨 재로그인 기회가 없다.
 *   그 외 실패 → 홈으로. 포럼 쪽 설정(시크릿 불일치 등) 문제라 사용자가 할 수 있는 게 없다.
 *
 * HomeLayout 밖의 형제 라우트다 — 헤더·푸터를 그렸다 지우는 깜빡임을 만들지 않는다.
 */
const DiscourseSsoPage = () => {
	// hook
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	React.useEffect(() => {
		// 포럼 로그아웃 착지. 서명이 없는 요청이라 SSO 핸드셰이크보다 먼저 본다.
		if (searchParams.get('logout') !== null) {
			clearSiteSession()
			persistor.purge().finally(() => window.location.replace(FORUM_URL))
			return
		}

		const sso: string = searchParams.get('sso') ?? ''
		const sig: string = searchParams.get('sig') ?? ''
		if (sso === '' || sig === '') {
			navigate('/', { replace: true })
			return
		}

		const here = `${window.location.pathname}${window.location.search}`
		const goLogin = () => {
			sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, here)
			navigate('/customer/login', { replace: true })
		}

		const token: string = pickSiteToken()
		if (token === '') {
			goLogin()
			return
		}

		customerApi
			.discourseConnect({
				body: { sso: sso, sig: sig },
				customHeaders: { Authorization: `Bearer ${token}` },
			})
			.then((res: ResType | undefined) => {
				const redirectUrl: unknown = res?.data?.[0]?.redirectUrl
				if (typeof redirectUrl === 'string' && redirectUrl.startsWith(FORUM_URL)) {
					window.location.replace(redirectUrl)
					return
				}
				if (res?.status === 401) {
					dropSiteToken()
					goLogin()
					return
				}
				Logger.warn(`[sso] discourse-connect failed: ${res?.status}`)
				navigate('/', { replace: true })
			})
			.catch((error: unknown) => {
				/*
					요청 자체가 실패한 경우(네트워크 차단, 서버 미도달). 이게 없으면 스피너가
					영원히 돈다 — 포럼으로도, 홈으로도 가지 못한 채 화면이 멈춘다.
				*/
				Logger.warn(`[sso] discourse-connect error: ${String(error)}`)
				navigate('/', { replace: true })
			})
	}, [])

	return <Spinner />
}

export default DiscourseSsoPage
